import { ToastMessage } from '../../../const';

export default {
    data: () => ({
        /**
         * @type {{
         *     isErrorDebug: boolean,
         *     isErrorShortMode: boolean
         *     isLoading: boolean,
         *     shouldInvalidate: boolean,
         *     error: null | Error,
         *     limit: number,
         *     offset: number,
         *     result: Partial<IDatasetResult>
         * }}
         */
        datasetPreview: {
            isErrorDebug: false,
            isErrorShortMode: true,
            isLoading: false,
            shouldInvalidate: true,
            limit: 20,
            offset: 0,
            result: {
                rowCount: 0,
                rows: []
            },
            error: null
        }
    }),
    computed: {
        previewLimit() {
            return this.datasetPreview.limit;
        },
        previewIsBusy() {
            return this.datasetPreview.isLoading;
        },
        previewIsUpdateEnabled() {
            const { datasetPreview } = this;
            return !datasetPreview.isLoading && datasetPreview.shouldInvalidate;
        },
        /**
         * @return {{ name:string, alias:string }[]}
         */
        previewColumns() {
            const { errorEntityNames } = this;
            const { drilldowns, order } = this.queryModel;
            const drilldownDimensions = drilldowns.reduce(
                (acc, { name, dimensions }) => ({ ...acc, [name]: dimensions[0] }),
                {}
            );

            return order
                .filter((name) => !errorEntityNames.includes(name))
                .map((name) => ({
                    name,
                    alias: drilldownDimensions[name] ?? name
                }));
        },
        /**
         * @return {string}
         */
        previewErrorMessage() {
            return this.datasetPreview.error ? this.$t(this.datasetPreview.error.message) : '';
        },
        /**
         * @return {string}
         */
        previewErrorMessageAdaptive() {
            const { datasetPreview, previewErrorMessage } = this;
            const { isErrorShortMode } = datasetPreview;
            return isErrorShortMode ? previewErrorMessage.split('\n')[0] : previewErrorMessage;
        }
    },
    methods: {
        previewFetchDataCancel() {
            const { request } = this;
            if (request?.isLoading) {
                request.cancel();
            }
        },
        /**
         * @param {{ offset:number, limit:number }} options
         * @return {Promise<IDatasetResult>}
         */
        previewFetchData({ offset, limit }) {
            const request = this.request;
            request.limit = limit;
            request.offset = offset;
            this.previewFetchDataCancel();

            return request.send({ debug: true });
        },
        /**
         * @param {number} offset
         * @param {number} limit
         * @param {number} rowCount
         * @return {number}
         */
        previewClampOffset(offset, limit, rowCount) {
            if (offset <= 0 || rowCount <= 0) {
                return 0;
            }
            if (offset < rowCount) {
                return offset;
            }
            return Math.floor((rowCount - 1) / limit) * limit;
        },
        async previewUpdate() {
            const { queryModel, datasetPreview } = this;
            let { offset } = datasetPreview;
            const { limit } = datasetPreview;
            const { metrics, dimensions } = queryModel;
            if (metrics.length === 0 && dimensions.length === 0) {
                return;
            }

            datasetPreview.error = null;
            datasetPreview.isLoading = true;
            try {
                let result = await this.previewFetchData({ offset, limit });
                const rowCount = result.rowCount ?? 0;
                const clampedOffset = this.previewClampOffset(offset, limit, rowCount);
                if (clampedOffset !== offset) {
                    datasetPreview.offset = clampedOffset;
                    offset = clampedOffset;
                    result = await this.previewFetchData({ offset, limit });
                }
                // bug when empty result – 'schema' interface changed
                if (result.rowCount === 0) {
                    result.schema = this.datasetPreview.result.schema;
                }
                this.datasetPreview.result = result;
                datasetPreview.shouldInvalidate = false;
            } catch (error) {
                if (error.isCancel) {
                    return;
                }
                datasetPreview.result = {
                    rowCount: 0,
                    rows: []
                };
                datasetPreview.error = error;
                this.$showToast({ text: ToastMessage.ERROR_OCCURED() });
            } finally {
                datasetPreview.isLoading = false;
            }
        },
        async previewExecute() {
            this.datasetPreview.offset = 0;
            await this.previewUpdate();
        },
        /**
         *
         */
        previewUpdateCancel() {
            this.previewFetchDataCancel();
        },
        previewInvalidate() {
            this.datasetPreview.shouldInvalidate = true;
        },
        /**
         * @param {{ page:number, offset: number }} options
         */
        onDatasetPreviewPageChange({ offset, limit }) {
            this.datasetPreview.offset = offset;
            this.datasetPreview.limit = limit;
            this.previewUpdate();
        }
    }
};
